import React, { useEffect, useState } from 'react';
import { computePadding } from './BlockRenderer';
import { resolveEmailImageUrl } from '../utils/emailAssetUrl';
import {
  HERO_PALETTE_SPLIT_IMAGE_HEIGHT,
  HERO_PRESET_IMAGES,
  isHeroCoreTemplate,
  normalizeHeroButtonRadius,
  resolveHeroContentStyles,
  type HeroContentTemplate,
} from '../utils/heroContentUtils';
import { HeroContentExtendedBlock } from './heroContentExtendedBlock';

type Props = {
  content: any;
  styles: any;
  globalFontFamily?: string;
  isMobile?: boolean;
  isPalettePreview?: boolean;
};

const LEGACY_HERO_IMAGE_URLS: Record<string, string> = {
  'https://images.unsplash.com/photo-1522202222206-72cc1c482f5a?auto=format&fit=crop&w=1200&q=80':
    HERO_PRESET_IMAGES.split,
  'https://images.unsplash.com/photo-1522202222206-72cc1c482f5a':
    HERO_PRESET_IMAGES.split,
};

function resolveBuilderImageSrc(src: string): string {
  const trimmed = src.trim();
  if (!trimmed) return '';
  const migrated = LEGACY_HERO_IMAGE_URLS[trimmed] || trimmed;
  if (/^(https?:\/\/|data:|blob:)/i.test(migrated)) return migrated;
  return resolveEmailImageUrl(migrated);
}

const HeroImagePlaceholder: React.FC<{ minHeight?: number; bg?: string }> = ({
  minHeight = 180,
  bg = '#DBEAFE',
}) => (
  <div
    style={{
      width: '100%',
      minHeight,
      backgroundColor: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    aria-hidden
  >
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="1.5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  </div>
);

const HeroImage: React.FC<{
  src: string;
  alt?: string;
  fill?: boolean;
  minHeight?: number;
}> = ({ src, alt = 'Hero', fill = false, minHeight = 180 }) => {
  const [failed, setFailed] = useState(false);
  const resolved = resolveBuilderImageSrc(src);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!resolved || failed) {
    return <HeroImagePlaceholder minHeight={minHeight} />;
  }

  return (
    <img
      key={resolved}
      src={resolved}
      alt={alt}
      className="hero-content-image"
      referrerPolicy="no-referrer"
      style={{
        width: '100%',
        height: fill ? '100%' : 'auto',
        minHeight: fill ? minHeight : undefined,
        maxHeight: fill ? minHeight : undefined,
        objectFit: fill ? 'cover' : undefined,
        objectPosition: 'center center',
        display: 'block',
      }}
      onError={() => setFailed(true)}
    />
  );
};

const HeroButton: React.FC<{
  content: any;
  r: ReturnType<typeof resolveHeroContentStyles>;
  align: 'left' | 'center' | 'right';
  compact?: boolean;
}> = ({ content, r, align, compact = false }) => {
  if (!content.buttonText || content.showButton === false) return null;
  const radius = normalizeHeroButtonRadius(content.buttonBorderRadius, r.buttonRadius);
  const btnPad = compact ? '5px 10px' : String(content.buttonPadding || '12px 24px');
  const isOutline = content.buttonStyle === 'outline';
  const isGhost = content.buttonStyle === 'ghost';
  return (
    <div className="hero-content-button-wrap" style={{ textAlign: align }}>
      <a
        href={String(content.buttonUrl || '#')}
        className="hero-content-button"
        onClick={(e) => e.preventDefault()}
        style={{
          display: 'inline-block',
          backgroundColor: isOutline || isGhost ? 'transparent' : r.buttonBg,
          color: isOutline || isGhost ? r.buttonBg : r.buttonColor,
          border: isOutline ? `1px solid ${r.buttonBg}` : 'none',
          padding: btnPad,
          borderRadius: radius,
          fontSize: r.buttonFontSize,
          fontWeight: 600,
          textDecoration: isGhost ? 'underline' : 'none',
          fontFamily: r.fontFamily,
          lineHeight: 1.2,
        }}
      >
        {String(content.buttonText)}
      </a>
    </div>
  );
};

const paletteTextClamp: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

export const HeroContentCoreBlock: React.FC<Props> = ({
  content,
  styles,
  globalFontFamily,
  isMobile = false,
  isPalettePreview = false,
}) => {
  const template = (content.template as string) || 'standard';
  if (!isHeroCoreTemplate(template)) return null;

  const r = resolveHeroContentStyles(
    template,
    content,
    styles,
    globalFontFamily,
    isMobile,
    isPalettePreview
  );
  const image = content.image ? String(content.image) : '';
  const splitImageMinH = isPalettePreview
    ? HERO_PALETTE_SPLIT_IMAGE_HEIGHT
    : isMobile
      ? 160
      : 220;
  const paletteImgH = isPalettePreview ? 52 : 180;
  const wrapClass = (base: string) =>
    `${base}${isPalettePreview ? ' hero-palette-preview' : ''}`;

  if (template === 'standard') {
    return (
      <div
        className={wrapClass('hero-content-wrap hero-content-standard')}
        style={{
          padding: computePadding(styles, '0px'),
          backgroundColor: r.containerBg,
          fontFamily: r.fontFamily,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {image && (
          <div
            style={{
              padding: 0,
              backgroundColor: '#F3F4F6',
              textAlign: 'center',
              maxHeight: isPalettePreview ? paletteImgH : undefined,
              overflow: 'hidden',
            }}
          >
            <HeroImage src={image} fill={isPalettePreview} minHeight={paletteImgH} />
          </div>
        )}
        {content.heading && (
          <div style={{ padding: r.headingPadding, backgroundColor: r.contentBg }}>
            <h1
              className="hero-content-heading"
              style={{
                margin: isPalettePreview ? '0 0 4px 0' : '0 0 20px 0',
                fontSize: r.headingSize,
                fontWeight: 700,
                color: r.headingColor,
                fontFamily: r.fontFamily,
                lineHeight: 1.15,
                ...(isPalettePreview ? paletteTextClamp : {}),
              }}
            >
              {String(content.heading)}
            </h1>
          </div>
        )}
        {content.text && (
          <div style={{ padding: r.textPadding, backgroundColor: r.contentBg }}>
            <p
              className="hero-content-text"
              style={{
                margin: 0,
                fontSize: r.textSize,
                color: r.textColor,
                lineHeight: '140%',
                fontFamily: r.fontFamily,
                ...(isPalettePreview ? paletteTextClamp : {}),
              }}
            >
              {String(content.text)}
            </p>
          </div>
        )}
        {(content.buttonText && content.showButton !== false) && (
          <div style={{ padding: r.buttonSectionPadding, backgroundColor: r.contentBg }}>
            <HeroButton
              content={content}
              r={r}
              align={isMobile ? 'center' : r.buttonAlign}
              compact={isPalettePreview}
            />
          </div>
        )}
      </div>
    );
  }

  if (template === 'dark-centered') {
    return (
      <div
        className={wrapClass('hero-content-wrap hero-content-dark-centered')}
        style={{
          padding: computePadding(styles, '0px'),
          backgroundColor: r.containerBg,
          fontFamily: r.fontFamily,
          textAlign: 'center',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {content.heading && (
          <div style={{ padding: r.headingPadding }}>
            <h1
              className="hero-content-heading"
              style={{
                margin: 0,
                fontSize: r.headingSize,
                fontWeight: 700,
                color: r.headingColor,
                fontFamily: r.fontFamily,
                lineHeight: 1.15,
                ...(isPalettePreview ? paletteTextClamp : {}),
              }}
            >
              {String(content.heading)}
            </h1>
          </div>
        )}
        {content.text && (
          <div style={{ padding: r.textPadding }}>
            <p
              className="hero-content-text"
              style={{
                margin: 0,
                fontSize: r.textSize,
                color: r.textColor,
                lineHeight: '140%',
                fontFamily: r.fontFamily,
                ...(isPalettePreview ? paletteTextClamp : {}),
              }}
            >
              {String(content.text)}
            </p>
          </div>
        )}
        {(content.buttonText && content.showButton !== false) && (
          <div style={{ padding: r.buttonSectionPadding }}>
            <HeroButton content={content} r={r} align="center" compact={isPalettePreview} />
          </div>
        )}
      </div>
    );
  }

  if (template === 'split') {
    const textCol = (
      <div
        style={{
          padding: r.splitTextPadding,
          backgroundColor: r.contentBg,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: isPalettePreview ? undefined : splitImageMinH,
        }}
      >
        {content.heading && (
          <h1
            className="hero-content-heading"
            style={{
              margin: isPalettePreview ? '0 0 4px 0' : '0 0 12px 0',
              fontSize: r.headingSize,
              fontWeight: 700,
              color: r.headingColor,
              fontFamily: r.fontFamily,
              lineHeight: 1.15,
              ...(isPalettePreview ? paletteTextClamp : {}),
            }}
          >
            {String(content.heading)}
          </h1>
        )}
        {content.text && (
          <p
            className="hero-content-text"
            style={{
              margin: isPalettePreview ? '0 0 6px 0' : '0 0 20px 0',
              fontSize: r.textSize,
              color: r.textColor,
              lineHeight: '140%',
              fontFamily: r.fontFamily,
              ...(isPalettePreview ? paletteTextClamp : {}),
            }}
          >
            {String(content.text)}
          </p>
        )}
        {(content.buttonText && content.showButton !== false) && (
          <HeroButton content={content} r={r} align="left" compact={isPalettePreview} />
        )}
      </div>
    );

    const imageCol = (
      <div
        style={{
          padding: r.splitImagePadding,
          backgroundColor: r.splitImageBg,
          boxSizing: 'border-box',
          height: splitImageMinH,
          minHeight: splitImageMinH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {image ? (
          <HeroImage src={image} fill minHeight={splitImageMinH} />
        ) : (
          <HeroImagePlaceholder minHeight={splitImageMinH} bg={r.splitImageBg} />
        )}
      </div>
    );

    return (
      <div
        className={wrapClass('hero-content-wrap hero-content-split')}
        style={{
          padding: computePadding(styles, '0px'),
          backgroundColor: r.containerBg,
          fontFamily: r.fontFamily,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            width: '100%',
            alignItems: 'stretch',
          }}
        >
          {isMobile ? (
            <>
              <div>{imageCol}</div>
              <div>{textCol}</div>
            </>
          ) : (
            <>
              <div>{textCol}</div>
              <div>{imageCol}</div>
            </>
          )}
        </div>
      </div>
    );
  }

  const EXTENDED: HeroContentTemplate[] = [
    'centered-light',
    'banner-image',
    'accent-line',
    'overlay-minimal',
  ];
  if (EXTENDED.includes(template as HeroContentTemplate)) {
    return (
      <HeroContentExtendedBlock
        template={template as 'centered-light' | 'banner-image' | 'accent-line' | 'overlay-minimal'}
        content={content}
        styles={styles}
        r={r}
        image={image}
        isMobile={isMobile}
        isPalettePreview={isPalettePreview}
      />
    );
  }

  if (template !== 'minimal') return null;

  return (
    <div
      className={wrapClass('hero-content-wrap hero-content-minimal')}
      style={{
        padding: computePadding(styles, '0px'),
        backgroundColor: r.containerBg,
        fontFamily: r.fontFamily,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {content.heading && (
        <div style={{ padding: r.minimalHeadingPadding }}>
          <h1
            className="hero-content-heading"
            style={{
              margin: 0,
              fontSize: r.headingSize,
              fontWeight: 700,
              color: r.headingColor,
              textAlign: r.textAlign,
              fontFamily: r.fontFamily,
              lineHeight: 1.15,
              ...(isPalettePreview ? paletteTextClamp : {}),
            }}
          >
            {String(content.heading)}
          </h1>
        </div>
      )}
      {content.text && (
        <div style={{ padding: r.minimalTextPadding }}>
          <p
            className="hero-content-text"
            style={{
              margin: 0,
              fontSize: r.textSize,
              color: r.textColor,
              lineHeight: '140%',
              textAlign: r.textAlign,
              fontFamily: r.fontFamily,
              ...(isPalettePreview ? paletteTextClamp : {}),
            }}
          >
            {String(content.text)}
          </p>
        </div>
      )}
      {(content.buttonText && content.showButton !== false) && (
        <div style={{ padding: isPalettePreview ? '0 10px 8px' : '0 20px 32px' }}>
          <HeroButton content={content} r={r} align={r.buttonAlign} compact={isPalettePreview} />
        </div>
      )}
    </div>
  );
};
